import { useMemo, useState } from 'react';
import type { RiskSearchFilter } from '../types';

interface RiskMatrixPickerProps {
  /** Unique risk codes across every Risk Scenario Catalog, sorted numerically. */
  riskCodes: string[];
  riskFilters: Record<string, RiskSearchFilter>;
  selectedMatrixPositions: Set<string>;
  onToggleRisk: (riskCode: string, selected: boolean) => void;
  onSetRiskPosition: (
    riskCode: string,
    probabilityCode: string,
    severityCode: string,
  ) => void;
  onBulkSetPosition: (probabilityCode: string, severityCode: string) => void;
  onToggleMatrixPosition: (
    probabilityCode: string,
    severityCode: string,
  ) => void;
  onClearPositions: () => void;
}

const PROBABILITY_ROWS = [
  { code: 'A', label: 'A - Highly probable (9-10)' },
  { code: 'B', label: 'B - Probable (7-8)' },
  { code: 'C', label: 'C - Occasional (5-6)' },
  { code: 'D', label: 'D - Remote (3-4)' },
  { code: 'E', label: 'E - Improbable (1-2)' },
  { code: 'F', label: 'F - Highly improbable (0)' },
];

const SEVERITY_COLUMNS = [
  { code: '1', label: '1 - High (7-10)' },
  { code: '2', label: '2 - Medium (5-6)' },
  { code: '3', label: '3 - Low (3-4)' },
  { code: '4', label: '4 - Negligible (0-2)' },
];

function matrixBand(probabilityCode: string, severityCode: string): string {
  const probabilityIndex = PROBABILITY_ROWS.findIndex(
    row => row.code === probabilityCode,
  );
  const severityIndex = SEVERITY_COLUMNS.findIndex(
    column => column.code === severityCode,
  );
  if (
    probabilityIndex <= 1 ||
    (probabilityIndex === 2 && severityIndex <= 1) ||
    (probabilityIndex === 3 && severityIndex === 0)
  ) {
    return 'high';
  }
  if (probabilityIndex <= 3 || (probabilityIndex === 4 && severityIndex <= 1)) {
    return 'medium';
  }
  return 'low';
}

function cellKey(probabilityCode: string, severityCode: string): string {
  return `${probabilityCode}-${severityCode}`;
}

export function RiskMatrixPicker({
  riskCodes,
  riskFilters,
  selectedMatrixPositions,
  onToggleRisk,
  onSetRiskPosition,
  onBulkSetPosition,
  onToggleMatrixPosition,
  onClearPositions,
}: RiskMatrixPickerProps) {
  const [search, setSearch] = useState('');
  const [draggedRisk, setDraggedRisk] = useState<string | null>(null);
  const selectedCodes = Object.keys(riskFilters);
  const visibleCodes = useMemo(() => {
    const normalized = search.trim().toLowerCase();
    return [...riskCodes]
      .filter(code => !normalized || code.toLowerCase().includes(normalized))
      .sort((left, right) =>
        left.localeCompare(right, undefined, { numeric: true }),
      );
  }, [riskCodes, search]);

  const exactRisksAtCell = (probabilityCode: string, severityCode: string) =>
    selectedCodes.filter(code => {
      const filter = riskFilters[code];
      return (
        filter.probabilityCode === probabilityCode &&
        filter.severityCode === severityCode
      );
    });

  const handleDrop = (
    probabilityCode: string,
    severityCode: string,
    event: React.DragEvent<HTMLDivElement>,
  ) => {
    event.preventDefault();
    const riskCode =
      draggedRisk || event.dataTransfer.getData('text/risk-code');
    if (riskCode) onSetRiskPosition(riskCode, probabilityCode, severityCode);
    setDraggedRisk(null);
  };

  const exactPositionCount = selectedCodes.filter(code => {
    const filter = riskFilters[code];
    return filter.probabilityCode !== null && filter.severityCode !== null;
  }).length;
  const handleCellSelection = (
    probabilityCode: string,
    severityCode: string,
  ) => {
    if (selectedCodes.length > 0) {
      onBulkSetPosition(probabilityCode, severityCode);
    } else {
      onToggleMatrixPosition(probabilityCode, severityCode);
    }
  };
  let matrixSelectionSummary = 'No position filter — any matrix position';
  if (selectedCodes.length > 0) {
    matrixSelectionSummary =
      'Select a cell to apply its position to unpinned selected risks.';
  } else if (selectedMatrixPositions.size > 0) {
    const positionLabel =
      selectedMatrixPositions.size === 1 ? 'position' : 'positions';
    matrixSelectionSummary = `${selectedMatrixPositions.size} ${positionLabel} selected — click cells to change.`;
  }

  return (
    <section
      className="risk-library-card"
      aria-labelledby="find-applications-title"
    >
      <div className="risk-library-actions">
        <div>
          <h2 id="find-applications-title">Find Applications by Risk</h2>
          <p>
            Choose an exact risk ID, use the matrix by itself, or combine both
            filters to find affected applications.
          </p>
        </div>
        <button
          className="risk-library-button secondary"
          type="button"
          onClick={onClearPositions}
          disabled={
            selectedMatrixPositions.size === 0 && exactPositionCount === 0
          }
        >
          Clear positions
        </button>
      </div>

      <div className="risk-library-picker">
        <section
          className="risk-library-picker-panel"
          aria-labelledby="risk-id-search-title"
        >
          <h3 id="risk-id-search-title">Find applications by risk ID</h3>
          <p>
            Select one or more catalogue IDs for an exact risk search. Clear all
            selected IDs to search applications using only the matrix.
          </p>
          <input
            className="risk-library-input"
            type="search"
            value={search}
            placeholder="Filter risk IDs"
            aria-label="Filter risk IDs"
            onChange={event => setSearch(event.target.value)}
          />
          <div
            className="risk-library-risk-list"
            role="listbox"
            aria-label="Risk IDs"
          >
            {visibleCodes.length === 0 ? (
              <span className="risk-library-muted">
                No catalogue risk IDs match this filter.
              </span>
            ) : (
              visibleCodes.map(code => {
                const selected = Boolean(riskFilters[code]);
                const inputId = `risk-library-risk-${code}`;
                return (
                  <div
                    className="risk-library-risk-option"
                    key={code}
                    draggable
                    onDragStart={event => {
                      setDraggedRisk(code);
                      event.dataTransfer.effectAllowed = 'copy';
                      event.dataTransfer.setData('text/risk-code', code);
                    }}
                    onDragEnd={() => setDraggedRisk(null)}
                  >
                    <input
                      id={inputId}
                      type="checkbox"
                      checked={selected}
                      aria-label={`Select risk ${code}`}
                      onChange={event =>
                        onToggleRisk(code, event.target.checked)
                      }
                    />
                    <span>
                      <strong>{code}</strong>
                    </span>
                  </div>
                );
              })
            )}
          </div>
          <div className="risk-library-selected-summary">
            {selectedCodes.length === 0 ? (
              <span className="risk-library-muted">No risks selected.</span>
            ) : (
              selectedCodes.map(code => {
                const filter = riskFilters[code];
                const position =
                  filter.probabilityCode && filter.severityCode
                    ? ` at ${filter.probabilityCode}-${filter.severityCode}`
                    : ' — any matrix position';
                return (
                  <span className="risk-library-chip" key={code}>
                    {code}
                    {position}
                  </span>
                );
              })
            )}
          </div>
        </section>

        <section
          className="risk-library-matrix risk-library-matrix-panel"
          aria-labelledby="matrix-search-title"
        >
          <div className="risk-library-panel-heading">
            <div>
              <h3 id="matrix-search-title">
                Find risks by severity and probability
              </h3>
              <p>
                Click one or more cells to find applications with risks in those
                positions. A risk ID is optional.
              </p>
            </div>
            <span className="risk-library-muted risk-library-matrix-badge">
              Risk ID optional
            </span>
          </div>
          <p className="risk-library-muted">
            With risk IDs selected, a cell applies its position to unpinned
            risks. Pinned risks are unchanged by bulk clicks.
          </p>
          <p
            className="risk-library-muted risk-library-matrix-selection-summary"
            aria-live="polite"
          >
            {matrixSelectionSummary}
          </p>
          <div className="risk-library-matrix-grid">
            <div className="risk-library-matrix-header">
              Level of probability
            </div>
            {SEVERITY_COLUMNS.map(column => (
              <div className="risk-library-matrix-header" key={column.code}>
                {column.label}
              </div>
            ))}
            {PROBABILITY_ROWS.flatMap(row => [
              <div
                className="risk-library-matrix-header"
                key={`${row.code}-header`}
              >
                {row.label}
              </div>,
              ...SEVERITY_COLUMNS.map(column => {
                const key = cellKey(row.code, column.code);
                const cellRisks = exactRisksAtCell(row.code, column.code);
                const isTarget =
                  cellRisks.length > 0 || selectedMatrixPositions.has(key);
                const cellTitle =
                  selectedCodes.length > 0
                    ? `Apply ${key} to unpinned selected risks`
                    : `Find applications with risks at ${key}`;
                return (
                  <div
                    className={`risk-library-matrix-cell band-${matrixBand(
                      row.code,
                      column.code,
                    )}${isTarget ? ' is-target' : ''}`}
                    key={key}
                    role="button"
                    tabIndex={0}
                    onClick={() => handleCellSelection(row.code, column.code)}
                    onKeyDown={event => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        handleCellSelection(row.code, column.code);
                      }
                    }}
                    onDragOver={event => event.preventDefault()}
                    onDrop={event => handleDrop(row.code, column.code, event)}
                    title={cellTitle}
                  >
                    <span className="risk-library-matrix-cell-risks">
                      {cellRisks.length > 0 ? (
                        cellRisks.map(code => (
                          <span
                            className={`risk-library-matrix-risk${
                              draggedRisk === code ? ' is-dragging' : ''
                            }`}
                            draggable
                            key={code}
                            onDragStart={event => {
                              event.stopPropagation();
                              setDraggedRisk(code);
                              event.dataTransfer.effectAllowed = 'move';
                              event.dataTransfer.setData(
                                'text/risk-code',
                                code,
                              );
                            }}
                            onDragEnd={() => setDraggedRisk(null)}
                            title={`Drag ${code} to another matrix cell`}
                          >
                            <strong>{code}</strong>
                          </span>
                        ))
                      ) : (
                        <span className="risk-library-muted">—</span>
                      )}
                    </span>
                  </div>
                );
              }),
            ])}
          </div>
        </section>
      </div>
    </section>
  );
}
